import { defineStore } from 'pinia';
import { ref } from 'vue';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';
import { fetchAllCompanies } from '@/services/company-service';
import { getPage, getPerPage } from '@/composables/PaginationComposable';

const POOL_URL = '/v2/admin/companies/pool';

export const usePoolStore = defineStore('pool', () => {
    // state
    const pool = ref({
        name: null,
        description: null,
    });
    const pools = ref([]);
    const editPoolId = ref(null);
    const totalRecords = ref(null);
    const companyPools = ref(null);
    const params = ref({
        page_size: null,
        page: null,
    });
    const loading = ref(false);

    // helper
    const initPool = () => {
        pool.value = {
            name: null,
            description: null,
        };
    };

    // actions
    const setActivePoolDetails = (poolId) => {
        const poolDetails = pools.value.find((pool) => pool.id === poolId);
        pool.value = { ...poolDetails };
    };

    const fetchAllPools = async (params, list) => {

        let page_size = params?.page_size
        let page = params?.page

        if (typeof list !== 'undefined') {
            page_size = getPerPage(list)
            page = getPage(list) + 1
        }

        try {
            loading.value = true;
            const response = await GlobalRequest.getRequest(POOL_URL, {
                params: {
                    page_size: page_size,
                    page: page,
                    paginate: true,
                },
            });
            if (response.statusCode === 200) {

                pools.value = response.data.data;
                totalRecords.value = response.data.total;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all pool');
        } finally {
            loading.value = false;
        }
    };

    const createPool = async (body) => {
        try {
            const response = await GlobalRequest.postRequest(POOL_URL, body);
            if (response.statusCode === 201) {
                initPool();
                pools.value.push(response.data);
                showToast(response, 'Company Pool created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create company pool');
        }
    };

    const editPool = async (poolId) => {
        editPoolId.value = poolId;
        setActivePoolDetails(poolId);
    };

    const updatePool = async (id, body) => {
        try {
            const response = await GlobalRequest.patchRequest(`${POOL_URL}/${id}`, body);
            if (response.statusCode === 200) {
                initPool();
                showToast(response, 'Company Pool updated successfully');
                editPoolId.value = null;
                await fetchAllPools(params.value);
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update company pool');
        }
    };

    const deletePool = async (id) => {
        try {
            const response = await GlobalRequest.deleteRequest(`${POOL_URL}/${id}`);
            if (response.statusCode === 200) {
                pools.value = pools.value.filter((pool) => pool.id !== id);
                showToast(response, 'Company Pool deleted');
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete company pool');
        }
    };

    const setupPoolCompanies = async (id) => {
        try {
            companyPools.value = null;
            setActivePoolDetails(id);
            const targetCompanies = pool.value?.companies;
            const targetCompanyIds = targetCompanies?.map((company) => company.id);
            const allCompanies = await fetchAllCompanies();
            const sourceCompanies = allCompanies?.filter((company) => !targetCompanyIds?.includes(company.id));
            companyPools.value = [sourceCompanies, targetCompanies];
        } catch (error) {
            showToast({}, 'Error setting up company pool');
        }
    };

    const addCompanyToPool = async (poolId, companyId) => {
        try {
            const response = await GlobalRequest.postRequest(`${POOL_URL}/${poolId}/company/${companyId}`);
            if (response.statusCode === 201) {
                return true;
                // showToast(response, 'Company added to pool successfully');
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to add company ' + companyId + ' to the pool');
        }
    };

    const removeCompanyFromPool = async (poolId, companyId) => {
        try {
            const response = await GlobalRequest.deleteRequest(`${POOL_URL}/${poolId}/company/${companyId}`);
            if (response.statusCode === 200) {
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to remove company ' + companyId + ' from the pool');
        }
    };

    return {
        // states
        pool,
        editPoolId,
        pools,
        companyPools,
        params,
        loading,
        totalRecords,
        // actions
        setActivePoolDetails,
        fetchAllPools,
        createPool,
        editPool,
        updatePool,
        deletePool,
        setupPoolCompanies,
        addCompanyToPool,
        removeCompanyFromPool,
    };
});
